import { createSlice } from '@reduxjs/toolkit'

export const initialState = {
  id: null,
  firstname: null,
  lastname: null,
  email: null,
  candidateIds: [],
  avatar: '',
  googleId: '',
  linkedinUrl: '',
  githubUrl: '',
  phoneNumber: '',
  likes: {
    pending: [],
    approved: [],
    rejected: [],
  },
  remoteWorking: [],
  researchStatus: 'unknown',
  jobPosition: {
    current: '',
    wanted: '',
  },
  company: {
    id: null,
    name: null,
    logo: null,
    description: null,
    primaryColor: null,
    secondaryColor: null,
  },
  location: {
    formattedAddress: null,
    city: null,
    state: null,
    country: null,
  },
  contractTypes: ['CDI'],
  schoolLevel: '2',
  skills: [],
  salaryRange: {
    minValue: 20,
    maxValue: 80,
  },
  matchingOffers: [],
  lastUpdatedOffers: null,
  experience: 'junior',
  resume: {},
  files: [],
  jobDomain: '',
  subJob: '',
  sector: '',
  subSector: '',
  active: false,
  isVerified: false,
  researchStatusUpdatedAt: null,
  preferredLanguage: 'en-EN',
  hasMatched: false,
}

export const talentSlice = createSlice({
  name: 'talent',
  initialState,
  reducers: {
    setTalent: (state, action) => {
      const {
        _id: id,
        firstname,
        lastname,
        email,
        remoteWorking,
        researchStatus,
        researchStatusUpdatedAt,
        skills,
        schoolLevel,
        location,
        contractTypes,
        experience,
        salaryRange,
        files = [],
        company,
        jobDomain,
        subJob,
        sector,
        subSector,
        active,
        isVerified,
        avatar,
        googleId,
        candidateIds,
        linkedinUrl,
        githubUrl,
        phoneNumber,
      } = action.payload

      if (
        candidateIds.length &&
        typeof candidateIds[0].candidateId === 'string'
      ) {
        state.candidateIds = candidateIds
      } else {
        state.candidateIds = candidateIds.map((el) => ({
          candidateId: el.candidateId._id,
          companyId: el.companyId,
        }))
      }

      state.id = id || initialState.id
      state.firstname = firstname || initialState.firstname
      state.lastname = lastname || initialState.lastname
      state.email = email || initialState.email
      state.remoteWorking = remoteWorking || initialState.remoteWorking
      state.researchStatus = researchStatus || initialState.researchStatus
      state.contractTypes = contractTypes || initialState.contractTypes
      state.skills = skills || initialState.skills
      state.location = location || initialState.location
      state.experience = experience || initialState.experience
      state.salaryRange = salaryRange || initialState.salaryRange
      state.resume = files[files.length - 1] || initialState.resume
      state.likes = initialState.likes
      state.company = company || initialState.company
      state.jobDomain = jobDomain || initialState.jobDomain
      state.researchStatusUpdatedAt =
        researchStatusUpdatedAt || initialState.researchStatusUpdatedAt
      state.active = active || initialState.active
      state.googleId = googleId || initialState.googleId
      state.avatar = avatar || initialState.avatar
      state.isVerified = isVerified || initialState.isVerified
      state.linkedinUrl = linkedinUrl || initialState.linkedinUrl
      state.githubUrl = githubUrl || initialState.githubUrl
      state.phoneNumber = phoneNumber || initialState.phoneNumber
      state.files = files || initialState.files
      state.schoolLevel = schoolLevel || initialState.schoolLevel
      state.subJob = subJob || initialState.subJob
      state.sector = sector || initialState.sector
      state.subSector = subSector || initialState.subSector
    },
    setTalentId: (state, action) => {
      state.id = action.payload
    },
    setRemoteWorking: (state, action) => {
      state.remoteWorking = action.payload
    },
    setResearchStatus: (state, action) => {
      state.researchStatus = action.payload
    },
    setMatchingOffers: (state, action) => {
      state.matchingOffers = action.payload
    },
    setLastUpdatedOffers: (state) => {
      state.lastUpdatedOffers = new Date().getTime()
    },
    setContractTypes: (state, action) => {
      state.contractTypes = action.payload
    },
    setExperience: (state, action) => {
      state.experience = action.payload
    },

    setSalaryRange: (state, action) => {
      state.salaryRange = action.payload
    },
    setJobDomain: (state, action) => {
      state.jobDomain = action.payload
    },
    setSubJob: (state, action) => {
      state.subJob = action.payload
    },
    setSector: (state, action) => {
      state.sector = action.payload
    },
    setSubSector: (state, action) => {
      state.subSector = action.payload
    },
    setLocation: (state, action) => {
      state.location = action.payload
    },
    setActive: (state, action) => {
      state.active = action.payload
    },
    setCompany: (state, action) => {
      state.company = action.payload
    },
    setResearchStatusUpdatedAt: (state, action) => {
      state.researchStatusUpdatedAt = action.payload
    },
    setPreferredLanguage: (state, action) => {
      state.preferredLanguage = action.payload
    },
    setLikes: (state, action) => {
      state.likes = {
        ...state.likes,
        ...action.payload,
      }
    },
    setPendingLikes: (state, action) => {
      state.likes.pending = action.payload
    },
    setApprovedLikes: (state, action) => {
      state.likes.approved = action.payload
    },
    setRejectedLikes: (state, action) => {
      state.likes.rejected = action.payload
    },
    setResume: (state, action) => {
      state.resume = action.payload
    },
    setLinkedinUrl: (state, action) => {
      state.linkedinUrl = action.payload
    },
    setGithubUrl: (state, action) => {
      state.githubUrl = action.payload
    },
    setPhoneNumber: (state, action) => {
      state.phoneNumber = action.payload
    },
    setEmail: (state, action) => {
      state.email = action.payload
    },
    setSchoolLevel: (state, action) => {
      state.schoolLevel = action.payload
    },
  },
})

export const {
  setTalent,
  setResearchStatus,
  setMatchingOffers,
  setLastUpdatedOffers,
  setContractTypes,
  selectedRemoteWorking,
  setExperience,
  setRemoteWorking,
  setSalaryRange,
  setLikes,
  setJobDomain,
  setActive,
  setLocation,
  setCompany,
  setResearchStatusUpdatedAt,
  setPreferredLanguage,
  setPendingLikes,
  setApprovedLikes,
  setCurrentMatchingOffers,
  setRejectedLikes,
  setResume,
  setLinkedinUrl,
  setGithubUrl,
  setPhoneNumber,
  setTalentId,
  setEmail,
  setSchoolLevel,
  setSubJob,
  setSector,
  setSubSector,
} = talentSlice.actions

export default talentSlice.reducer
